import { angleDelta, headingOf, type V2 } from '../core/math';
import type { Rng } from '../core/rng';
import { edgeLength, lanePoint } from '../world/roads';
import type { CityLayout, RoadEdge } from '../world/types';

/**
 * Lane-level view of the road graph, built once from the CityLayout. Traffic follows lanes (straight
 * segments from just outside one junction to just outside the next) joined by curved connectors
 * through the junction box. Everything here is pure and deterministic.
 */

export interface Lane {
  /** Dense id == index in `RoadNet.lanes`. */
  id: number;
  edgeId: number;
  /** True when travelling from -> to along the edge. */
  forward: boolean;
  /** Lane index within the direction (0 = nearest the centre line). */
  index: number;
  fromNode: number;
  toNode: number;
  /** Start / end of the drivable section (trimmed to the junction boxes). `b` is the stop line. */
  a: V2;
  b: V2;
  /** Unit travel direction. */
  dir: V2;
  /** Unit vector to the LEFT of travel (kerb side on British roads). */
  left: V2;
  length: number;
  speedLimit: number;
  alley: boolean;
  districtId: string;
  /** Intersection at the end of this lane (null if the end node has none). */
  endIntersection: number | null;
  laneWidth: number;
  /** Half the carriageway width (m). */
  roadHalfWidth: number;
  /** Lateral distance (m) from the lane centre to the left kerb. */
  kerbOffset: number;
}

export type TurnKind = 'straight' | 'left' | 'right' | 'uturn';

export interface Connector {
  from: Lane;
  to: Lane;
  /** Sampled curve (inclusive of both ends). */
  pts: V2[];
  /** Cumulative length at each point (cum[0] = 0). */
  cum: number[];
  length: number;
  /** Signed heading change (rad), > 0 = left turn. */
  turn: number;
  kind: TurnKind;
  /** Comfortable speed through the junction (m/s). */
  maxSpeed: number;
  /** Approximate turning radius of the curve (m); Infinity when straight. */
  radius: number;
}

/**
 * Weight of a lane by street class, for spawning (per metre) and for choosing the next lane at a
 * junction: main roads carry most of the traffic, side streets a steady trickle (docs/TRAFFIC.md "Spread").
 */
export function laneClassWeight(lane: { speedLimit: number; laneWidth: number }, sidewalk: number, main?: boolean): number {
  // Authored layouts flag their main roads; test layouts fall back to telling them apart by size and speed.
  if (main ?? (lane.speedLimit > 12 || lane.laneWidth >= 3.75 || sidewalk >= 4.5)) return 1.6; // arterial, quay, high street
  if (lane.speedLimit < 10) return 0.7; // narrow terraced street
  return 1;
}

const CONNECTOR_SAMPLES = 8;
/** Extra lane trim (m) beyond the carriageway half-width at a junction-less bend. */
const BEND_CLEARANCE = 5;
/** Arms deviating less than this from straight-on (rad) don't count as a bend. */
const BEND_MIN_TURN = 0.35;

export class RoadNet {
  readonly lanes: Lane[] = [];
  /** lanes leaving each node (travel start at node) */
  private readonly outLanes: Lane[][] = [];
  private readonly laneKey = new Map<number, number>();
  private readonly connectors = new Map<number, Connector>();
  private readonly conflictCache = new Map<number, boolean>();
  /** Intersection id for each node id (or null). */
  readonly nodeIntersection: (number | null)[] = [];
  /** Lane trim (m) at junction-less bend nodes (0 = none). */
  private readonly bendTrim: number[] = [];

  constructor(readonly layout: CityLayout) {
    const { nodes, edges } = layout.roads;
    for (let i = 0; i < nodes.length; i++) {
      this.outLanes.push([]);
      this.nodeIntersection.push(null);
      this.bendTrim.push(0);
    }
    for (const ix of layout.intersections) this.nodeIntersection[ix.nodeId] = ix.id;
    this.computeBendTrims();

    for (const e of edges) {
      for (let i = 0; i < e.lanesForward; i++) this.addLane(e, true, i);
      if (!e.oneWay) for (let i = 0; i < e.lanesBackward; i++) this.addLane(e, false, i);
    }
  }

  private trimFor(nodeId: number, len: number): number {
    const ixId = this.nodeIntersection[nodeId];
    if (ixId === null || ixId === undefined) {
      const bend = this.bendTrim[nodeId] ?? 0;
      return bend > 0 ? Math.min(bend, len * 0.4) : 0;
    }
    const ix = this.layout.intersections[ixId]!;
    return Math.min(ix.radius, len * 0.4);
  }

  /**
   * Junction-less nodes where the road changes direction (ring-road corners, chained edges) still need
   * a turning box: without one the lanes run right up to the node and the connector between them is a
   * ~1.5 m hook no car can follow. Trim = half the widest carriageway + BEND_CLEARANCE, so the inside
   * lane turns on a radius of roughly BEND_CLEARANCE metres. Collinear chains (straight-on) stay untrimmed.
   */
  private computeBendTrims(): void {
    const { nodes, edges } = this.layout.roads;
    const dirs: { x: number; z: number; halfW: number }[][] = nodes.map(() => []);
    for (const e of edges) {
      const a = nodes[e.from]!.pos;
      const b = nodes[e.to]!.pos;
      const l = Math.hypot(b.x - a.x, b.z - a.z) || 1;
      dirs[e.from]!.push({ x: (b.x - a.x) / l, z: (b.z - a.z) / l, halfW: e.width / 2 });
      dirs[e.to]!.push({ x: (a.x - b.x) / l, z: (a.z - b.z) / l, halfW: e.width / 2 });
    }
    for (let n = 0; n < nodes.length; n++) {
      const ds = dirs[n]!;
      if (ds.length < 2) continue;
      // Any pair of arms that is not (nearly) opposite = a real change of direction for some movement.
      let bend = false;
      for (let i = 0; i < ds.length && !bend; i++) {
        for (let j = i + 1; j < ds.length; j++) {
          if (ds[i]!.x * ds[j]!.x + ds[i]!.z * ds[j]!.z > -Math.cos(BEND_MIN_TURN)) {
            bend = true;
            break;
          }
        }
      }
      if (!bend) continue;
      let halfW = 0;
      for (const d of ds) halfW = Math.max(halfW, d.halfW);
      this.bendTrim[n] = halfW + BEND_CLEARANCE;
    }
  }

  private addLane(edge: RoadEdge, forward: boolean, index: number): void {
    const layout = this.layout;
    const len = edgeLength(layout, edge);
    const fromNode = forward ? edge.from : edge.to;
    const toNode = forward ? edge.to : edge.from;
    const trimA = this.trimFor(fromNode, len);
    const trimB = this.trimFor(toNode, len);
    const t0 = trimA / len;
    const t1 = 1 - trimB / len;
    const a = lanePoint(layout, edge, forward, index, t0);
    const b = lanePoint(layout, edge, forward, index, t1);
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const l = Math.hypot(dx, dz) || 1;
    const dir = { x: dx / l, z: dz / l };
    const lane: Lane = {
      id: this.lanes.length,
      edgeId: edge.id,
      forward,
      index,
      fromNode,
      toNode,
      a,
      b,
      dir,
      left: { x: dir.z, z: -dir.x },
      length: l,
      speedLimit: edge.speedLimit,
      alley: edge.alley,
      districtId: edge.districtId,
      endIntersection: this.nodeIntersection[toNode] ?? null,
      laneWidth: edge.laneWidth,
      roadHalfWidth: edge.width / 2,
      kerbOffset: 0,
    };
    // Distance from this lane's centre to the left kerb (in the lane's own travel frame).
    const n = edge.lanesForward;
    lane.kerbOffset = edge.width / 2 - (edge.oneWay ? index + 0.5 - n / 2 : index + 0.5) * edge.laneWidth;
    this.laneKey.set(laneKey(edge.id, forward, index), lane.id);
    this.lanes.push(lane);
    this.outLanes[fromNode]!.push(lane);
  }

  /** Turning box (m) lanes are trimmed by at a junction-less bend node; 0 for straight chains / junctions. */
  bendRadius(nodeId: number): number {
    return this.nodeIntersection[nodeId] === null ? (this.bendTrim[nodeId] ?? 0) : 0;
  }

  getLane(edgeId: number, forward: boolean, index = 0): Lane | undefined {
    const id = this.laneKey.get(laneKey(edgeId, forward, index));
    return id === undefined ? undefined : this.lanes[id];
  }

  /** The lane of (edgeId, forward) closest to the requested lane index. */
  laneNear(edgeId: number, forward: boolean, index: number): Lane | undefined {
    for (let k = index; k >= 0; k--) {
      const l = this.getLane(edgeId, forward, k);
      if (l) return l;
    }
    return this.getLane(edgeId, forward, 0);
  }

  lanesLeaving(nodeId: number): readonly Lane[] {
    return this.outLanes[nodeId] ?? [];
  }

  /**
   * Lanes a vehicle on `lane` may continue onto (one per outgoing edge-direction, index clamped).
   * U-turns are only offered at dead ends.
   */
  successors(lane: Lane, out: Lane[] = []): Lane[] {
    out.length = 0;
    const leaving = this.outLanes[lane.toNode]!;
    const seen = new Set<number>();
    let uturn: Lane | null = null;
    for (const m of leaving) {
      if (m.edgeId === lane.edgeId) {
        if (m.forward !== lane.forward && !uturn) uturn = this.laneNear(m.edgeId, m.forward, lane.index) ?? null;
        continue;
      }
      const k = m.edgeId * 2 + (m.forward ? 1 : 0);
      if (seen.has(k)) continue;
      seen.add(k);
      const pick = this.laneNear(m.edgeId, m.forward, lane.index);
      if (pick) out.push(pick);
    }
    if (out.length === 0 && uturn) out.push(uturn);
    return out;
  }

  /** Curved path through the junction from the end of `from` to the start of `to` (cached). */
  connector(from: Lane, to: Lane): Connector {
    const key = from.id * this.lanes.length + to.id;
    let c = this.connectors.get(key);
    if (!c) {
      c = buildConnector(from, to);
      this.connectors.set(key, c);
    }
    return c;
  }

  /** True if two connectors through the same junction pass within `clearance` metres of each other. */
  conflicts(a: Connector, b: Connector, clearance = 2.7): boolean {
    if (a === b) return true;
    const key = connectorId(a, this.lanes.length) * (this.lanes.length * this.lanes.length) + connectorId(b, this.lanes.length);
    const cached = this.conflictCache.get(key);
    if (cached !== undefined) return cached;
    let hit = false;
    // Merging into the same lane always conflicts.
    if (a.to === b.to) hit = true;
    const c2 = clearance * clearance;
    for (let i = 0; i < a.pts.length && !hit; i++) {
      const p = a.pts[i]!;
      for (let j = 0; j < b.pts.length; j++) {
        const q = b.pts[j]!;
        const dx = p.x - q.x;
        const dz = p.z - q.z;
        if (dx * dx + dz * dz < c2) {
          hit = true;
          break;
        }
      }
    }
    this.conflictCache.set(key, hit);
    this.conflictCache.set(connectorId(b, this.lanes.length) * (this.lanes.length * this.lanes.length) + connectorId(a, this.lanes.length), hit);
    return hit;
  }

  /** Random successor weighted towards straight-ahead on larger, non-alley roads. */
  /**
   * `minRadius`: the vehicle's tightest turning circle (m). Turns it physically can't make (a tipper
   * lorry into a tight left) are skipped unless they are the only way on.
   */
  pickSuccessor(lane: Lane, rng: Rng, minRadius = 0, classBias = true): Lane | null {
    let opts = this.successors(lane);
    if (minRadius > 0 && opts.length > 1) {
      const feasible = opts.filter((m) => this.connector(lane, m).radius * 1.25 >= minRadius);
      if (feasible.length > 0) opts = feasible;
    }
    if (opts.length === 0) return null;
    if (opts.length === 1) return opts[0]!;
    const weights = opts.map((m) => {
      const c = this.connector(lane, m);
      let w = c.kind === 'straight' ? 1.3 : 1;
      if (m.alley) w *= 0.12;
      const edge = this.layout.roads.edges[m.edgeId]!;
      w *= (0.6 + edge.width / 16) * (classBias ? Math.sqrt(laneClassWeight(m, edge.sidewalk, edge.main)) : 1);
      return w;
    });
    return rng.weighted(opts, weights);
  }

  /**
   * Cheapest route (list of lanes to take after `from`) ending on a lane that leaves `goalNode`'s
   * neighbourhood, i.e. arriving AT `goalNode`. Dijkstra over (edge, direction) states. `alleyCost`
   * multiplies an alley's travel time (3 for traffic; the police pass a higher one, docs/POLICE-AI-POLISH.md).
   */
  findRoute(from: Lane, goalNode: number, alleyCost = 3): Lane[] | null {
    if (from.toNode === goalNode) return [];
    const nStates = this.lanes.length;
    const dist = new Float64Array(nStates).fill(Infinity);
    const prev = new Int32Array(nStates).fill(-1);
    const done = new Uint8Array(nStates);
    dist[from.id] = 0;
    // Small graphs: a linear-scan priority queue is plenty and allocation-free.
    for (;;) {
      let best = -1;
      let bd = Infinity;
      for (let i = 0; i < nStates; i++) {
        if (!done[i] && dist[i]! < bd) {
          bd = dist[i]!;
          best = i;
        }
      }
      if (best < 0) return null;
      done[best] = 1;
      const lane = this.lanes[best]!;
      if (lane.toNode === goalNode) {
        const route: Lane[] = [];
        let cur = best;
        while (cur !== from.id) {
          route.push(this.lanes[cur]!);
          cur = prev[cur]!;
        }
        return route.reverse();
      }
      for (const m of this.successors(lane)) {
        const cost = (m.length / m.speedLimit) * (m.alley ? alleyCost : 1) + (this.connector(lane, m).kind === 'uturn' ? 8 : 0);
        const nd = bd + cost;
        if (nd < dist[m.id]!) {
          dist[m.id] = nd;
          prev[m.id] = best;
        }
      }
    }
  }

  /** Nearest lane whose direction roughly matches `heading` (any if heading omitted). */
  nearestLane(p: V2, heading?: number): { lane: Lane; s: number; lateral: number } | null {
    let best: { lane: Lane; s: number; lateral: number } | null = null;
    let bestScore = Infinity;
    const fwd = heading === undefined ? null : { x: Math.sin(heading), z: Math.cos(heading) };
    for (const l of this.lanes) {
      if (fwd && l.dir.x * fwd.x + l.dir.z * fwd.z < 0.3) continue;
      const rx = p.x - l.a.x;
      const rz = p.z - l.a.z;
      const s = rx * l.dir.x + rz * l.dir.z;
      const cs = s < 0 ? 0 : s > l.length ? l.length : s;
      const lateral = rx * l.left.x + rz * l.left.z;
      const along = s - cs;
      const score = lateral * lateral + along * along;
      if (score < bestScore) {
        bestScore = score;
        best = { lane: l, s: cs, lateral };
      }
    }
    return best;
  }
}

function laneKey(edgeId: number, forward: boolean, index: number): number {
  return (edgeId * 2 + (forward ? 1 : 0)) * 16 + index;
}

function connectorId(c: Connector, nLanes: number): number {
  return c.from.id * nLanes + c.to.id;
}

function buildConnector(from: Lane, to: Lane): Connector {
  const p0 = from.b;
  const p2 = to.a;
  const d1 = from.dir;
  const d2 = to.dir;
  const turn = angleDelta(headingOf(d1), headingOf(d2));
  const cross = d1.x * d2.z - d1.z * d2.x;
  const dx = p2.x - p0.x;
  const dz = p2.z - p0.z;
  const gap = Math.hypot(dx, dz);
  let cx: number;
  let cz: number;
  if (Math.abs(cross) < 0.12) {
    if (d1.x * d2.x + d1.z * d2.z > 0) {
      cx = (p0.x + p2.x) / 2;
      cz = (p0.z + p2.z) / 2;
    } else {
      // U-turn: swing out ahead of the stop line.
      const reach = Math.max(3, gap * 0.8);
      cx = (p0.x + p2.x) / 2 + d1.x * reach;
      cz = (p0.z + p2.z) / 2 + d1.z * reach;
    }
  } else {
    // Intersection of p0 + d1 t and p2 - d2 u.
    const t = (dx * d2.z - dz * d2.x) / cross;
    if (t < 0 || t > gap * 2) {
      cx = (p0.x + p2.x) / 2;
      cz = (p0.z + p2.z) / 2;
    } else {
      cx = p0.x + d1.x * t;
      cz = p0.z + d1.z * t;
    }
  }
  const pts: V2[] = [];
  const cum: number[] = [];
  let acc = 0;
  for (let i = 0; i <= CONNECTOR_SAMPLES; i++) {
    const u = i / CONNECTOR_SAMPLES;
    const w0 = (1 - u) * (1 - u);
    const w1 = 2 * (1 - u) * u;
    const w2 = u * u;
    const p = { x: w0 * p0.x + w1 * cx + w2 * p2.x, z: w0 * p0.z + w1 * cz + w2 * p2.z };
    if (i > 0) acc += Math.hypot(p.x - pts[i - 1]!.x, p.z - pts[i - 1]!.z);
    pts.push(p);
    cum.push(acc);
  }
  const abs = Math.abs(turn);
  const kind: TurnKind = abs > 2.5 ? 'uturn' : abs < 0.35 ? 'straight' : turn > 0 ? 'left' : 'right';
  const maxSpeed = kind === 'straight' ? Math.min(from.speedLimit, to.speedLimit) : kind === 'left' ? 7.5 : kind === 'right' ? 5.5 : 3.5;
  return { from, to, pts, cum, length: acc, turn, kind, maxSpeed: Math.min(maxSpeed, to.alley || from.alley ? 5 : maxSpeed), radius: kind === 'straight' ? Infinity : acc / Math.max(abs, 1e-3) };
}
